import { CheckOutlined } from '@ant-design/icons';
import { Col, DatePicker, Radio, Row, Tabs, Typography } from 'antd';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import MoneyFilled from '../../assets/icons/MoneyFilled';
import SubHeader from '../../components/SubHeader';
import { eventGA } from '../../helpers/googleAnalytics';
import { getRazaoSocial } from '../../helpers/shipper';
import { AuthConsumer } from '../../security/authContext';
import { getPermissions } from '../../security/authService';
import AderenciaCarregamento from './AderenciaCarregamento';
import { Styles } from './styles';
import VariacaoFrete from './VariacaoFrete';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

export default function DashboardPerformance() {
  const [ativo, setAtivo] = useState('WEEKLY');
  const [tab, setTab] = useState('none');
  const [openPicker, setOpenPicker] = useState(false);
  const [dataAlterada, setDataAlterada] = useState({});
  const [dates, setDate] = useState({
    dataInicial: moment()
      .startOf('isoWeeks')
      .add(-5, 'isoweek')
      .format(),
    dataFinal: moment()
      .endOf('isoWeeks')
      .format(),
  });
  const [subHeader, setSubHeader] = useState('ADERENCIA DE CARREGAMENTO');
  const [infoText, setInfoText] = useState(
    'Dados a partir de maio/2020. Atenção para dados parciais em períodos não fechados.'
  );
  const pickerRef = useRef(null);

  const setHeader = useCallback(header => {
    switch (header) {
      case 'variacaoFrete':
        setSubHeader('VARIAÇÃO DE FRETE');
        setInfoText(
          'Dados a partir de janeiro/2018. Atenção para dados parciais em períodos não fechados e para datas que não terão informações (linhas interrompidas).'
        );
        break;
      case 'aderencia':
      default:
        setSubHeader('ADERENCIA DE CARREGAMENTO');
        setInfoText(
          'Dados a partir de maio/2020. Atenção para dados parciais em períodos não fechados.'
        );
    }
  }, []);

  const getDatas = tipo => {
    let datas = {};
    switch (tipo) {
      case 'DAILY':
        datas = {
          dataInicial: moment()
            .startOf('days')
            .format(),
          dataFinal: moment().format(),
        };
        break;
      case 'MONTHLY':
        datas = {
          dataInicial: moment()
            .startOf('months')
            .add(-5, 'months')
            .format(),
          dataFinal: moment()
            .endOf('months')
            .format(),
        };
        break;
      default:
        datas = {
          dataInicial: moment()
            .startOf('isoWeeks')
            .add(-5, 'isoweek')
            .format(),
          dataFinal: moment()
            .endOf('isoWeeks')
            .format(),
        };
    }
    setDataAlterada({});
    setDate(datas);
  };

  const eventClickPicker = () => {
    setTimeout(() => {
      const input = document.getElementsByClassName('ant-picker-input');

      const clickEvent = () => {
        setOpenPicker(true);
      };

      input[0].addEventListener('click', clickEvent);
      input[1].addEventListener('click', clickEvent);
    }, 200);
  };

  const onChangeDateRangePicker = (datas = []) => {
    if (datas && datas.length > 1) {
      const [dataInicio, dataFim] = datas;
      const dataDiff = moment.duration(dataInicio.diff(dataFim)).asDays() * -1;

      let dataInicial;
      if (dataDiff > 5) {
        dataInicial = moment(dataFim)
          .startOf('days')
          .add(-5, 'days')
          .format();
      } else {
        dataInicial = moment(dataInicio)
          .startOf('days')
          .format();
      }

      const dataFinal = moment(dataFim)
        .endOf('days')
        .format();
      setDataAlterada({});
      setDate({ dataInicial, dataFinal });
    }

    setOpenPicker(false);
  };

  useEffect(() => {
    const possibleTabsPermissions = [
      {
        permission: 'dashboard-performance:aderencia-carregamento',
        tab: 'aderencia',
      },
      {
        permission: 'dashboard-performance:variacao-frete',
        tab: 'variacaoFrete',
      },
    ];
    const userPermissions = getPermissions();

    const defaultTab = possibleTabsPermissions.filter(possibleTab =>
      userPermissions.some(permission => possibleTab.permission === permission)
    );

    const actualTab = defaultTab.shift()?.tab || 'aderencia';
    setTab(actualTab);
    setHeader(actualTab);
    eventGA(
      'Shipper',
      'Dashboard de Perfomance',
      defaultTab.shift()?.tab || 'aderencia'
    );
  }, [setHeader]);

  const onChangeRadioGroup = tipo => {
    if (ativo !== tipo.target.value) {
      setAtivo(tipo.target.value);
      getDatas(tipo.target.value);
    }
  };

  const tabOperations = () => (
    <>
      <Radio.Group
        value="default"
        onChange={tipo => onChangeRadioGroup(tipo)}
        className="mt-10 mr-40"
      >
        <Radio.Button
          onClick={() => {
            setTimeout(() => pickerRef.current.focus(), 200);
            setOpenPicker(!openPicker);
            eventClickPicker();
          }}
          className={
            ativo === 'DAILY'
              ? 'tabAtivo radio-btn radiusLeft'
              : 'tabInativo radio-btn radiusLeft'
          }
          value="DAILY"
        >
          diário
        </Radio.Button>
        <Radio.Button
          value="WEEKLY"
          className={
            ativo === 'WEEKLY' ? 'tabAtivo radio-btn' : 'tabInativo radio-btn'
          }
        >
          semanal
        </Radio.Button>
        <Radio.Button
          value="MONTHLY"
          className={
            ativo === 'MONTHLY'
              ? 'tabAtivo radio-btn radiusRight'
              : 'tabInativo radio-btn radiusRight'
          }
        >
          mensal
        </Radio.Button>
        {ativo === 'DAILY' && (
          <Row className="mt-10">
            <RangePicker
              autoFocus={openPicker}
              bordered={false}
              open={openPicker}
              format="DD/MM/YYYY"
              onChange={datas => onChangeDateRangePicker(datas)}
              ref={pickerRef}
            />
          </Row>
        )}
      </Radio.Group>
    </>
  );

  /**
   * Retorna apenas os paíneis permitido para o usuário
   *
   *
   * @param {Object} user Informações do usuário
   */
  const performTabPane = user => {
    const allTabs = [
      <TabPane
        access="dashboard-performance:aderencia-carregamento"
        tab={
          <>
            <CheckOutlined />
            Aderência de carregamento
          </>
        }
        key="aderencia"
      >
        <AderenciaCarregamento
          tipo={ativo}
          tab={tab}
          datas={dates}
          setDate={setDate}
        />
      </TabPane>,
      <TabPane
        access="dashboard-performance:variacao-frete"
        tab={
          <>
            <MoneyFilled isActive={tab === 'variacaoFrete'} />
            Variação de frete
          </>
        }
        key="variacaoFrete"
      >
        <VariacaoFrete
          tipo={ativo}
          tab={tab}
          datas={dates}
          setDate={setDate}
          dataAlterada={dataAlterada}
          setDataAlterada={setDataAlterada}
        />
      </TabPane>,
    ];

    // filtro os paineis com base no acesso do usuário
    const OnlyTabAllowed = allTabs.filter(({ props: { access } }) =>
      user.permissions?.includes(access)
    );

    return OnlyTabAllowed;
  };

  const onChangeTab = tabSelected => {
    setHeader(tabSelected);
    setTab(tabSelected);
    setAtivo('WEEKLY');
    setDataAlterada({});
    setDate({
      dataInicial: moment()
        .startOf('isoWeeks')
        .add(-5, 'isoweek')
        .format(),
      dataFinal: moment()
        .endOf('isoWeeks')
        .format(),
    });
    eventGA('Shipper', 'Dashboard de Perfomance', tabSelected);
  };

  return (
    <>
      <Styles />
      <SubHeader title={`${subHeader} - ${getRazaoSocial()}`} />
      <Row justify="space-between">
        <Col xs={24} sm={24} md={24} lg={24} className="ml-15">
          <AuthConsumer>
            {({ user }) => (
              <Tabs
                onChange={tabSelected => onChangeTab(tabSelected)}
                tabBarExtraContent={tabOperations()}
              >
                {performTabPane(user).map(tabAllowed => tabAllowed)}
              </Tabs>
            )}
          </AuthConsumer>
        </Col>
      </Row>
      <Row justify="space-between" className="rowMensagem">
        <Col xs={24} sm={24} md={8} lg={9}>
          <Typography.Text>{infoText}</Typography.Text>
        </Col>
      </Row>
    </>
  );
}
